import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Modal } from 'ksnm-common-ui/lib/Modal';
import { Switch } from 'ksnm-common-ui/lib/Switch';
import { Table } from 'ksnm-common-ui/lib/Table2/Table';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';
import type { OnTableChange } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';

import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { numberWithSpaces } from '@src/utils/numberUtils';
import { constantStore } from '@stores/constantStore';
import { dataControlStore } from '@stores/dataControlStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { Breadcrumbs } from '../Breadcrumbs';
import { columns } from './columns';
import { useUpdateTitle } from './hooks/useUpdateTitle';
import { Title } from './Title';

import styles from './StepLevelModal.module.scss';

const { TABLE_NAMES } = constantStore;

export const StepLevelModal = observer(() => {
  const [count, setCount] = useState(0);
  const [show, setShow] = useState(false);
  const [onlyError, setOnlyError] = useState<boolean>(false);

  const { lastPagesParam, removePage, removeBreadcrumb } = dataControlStore;
  const { execNum, maxExecNum } = lastPagesParam;

  const [key, update] = useUpdateTable({ tableName: TABLE_NAMES.STEP_LEVEL });

  const onChangeTable = useCallback((v: OnTableChange) => {
    setCount(v.totalPhysicsRows);
  }, []);

  const { titleData, updateTitle } = useUpdateTitle();

  const title = useMemo(
    () => (
      <div className={styles.wrapper}>
        <Title data={titleData} />

        <div className={styles.row}>
          {!!count && <p className={styles.count}>Всего записей: {numberWithSpaces(count)}</p>}
          <div className={styles.switch}>
            <Switch checked={onlyError} onChange={setOnlyError} />
            Только ошибки
          </div>
        </div>
      </div>
    ),
    [count, onlyError, titleData],
  );

  const hardFilter = useMemo(() => {
    const out = [
      ...(lastPagesParam.filters?.[0] ?? []),
      { name: 'expr_type', type: FILTER.NEQ, values: ['premise'], filterIsInverted: false },
    ];

    if (onlyError) {
      out.push({ type: FILTER.EQ, name: 'status', values: ['F'], filterIsInverted: false });
    }

    return [out];
  }, [lastPagesParam.filters, onlyError]);

  const onClose = useCallback(() => {
    removePage();
    removeBreadcrumb();
  }, [removeBreadcrumb, removePage]);

  const onRefresh = useCallback(() => {
    update();
    updateTitle();
  }, [update, updateTitle]);

  const extendTableButtons = useMemo(
    () => (
      <>
        <IconButton key="refresh" iconName="refresh" title="Обновить" onClick={onRefresh} />
      </>
    ),
    [onRefresh],
  );

  const rowClassName = useCallback(() => (maxExecNum === execNum ? '' : styles.bgRowYellow), [execNum, maxExecNum]);

  const afterOpenChange = useCallback((open) => {
    setShow(open);
  }, []);

  return (
    <Modal
      open
      className={styles.modal}
      centered
      footer={null}
      onCancel={onClose}
      width="98vw"
      closeIcon={<Icon name="close" noFill height={24} />}
      afterOpenChange={afterOpenChange}
    >
      <div className={styles.container}>
        <Breadcrumbs className={styles.breadcrumbs} withoutAction />

        {show && Object.keys(titleData).length > 0 && (
          <Table
            key={key}
            columns={columns}
            tableTitle={title}
            tableName={TABLE_NAMES.STEP_LEVEL}
            resource="v_ctrl_step"
            externalFetch={ksnmTableStore.externalFetch}
            externalFetchXLSX={ksnmTableStore.getExcelStreamV2}
            hardFilter={hardFilter}
            onTableChange={onChangeTable}
            showAggregateRow
            extendTableButtons={extendTableButtons}
            rowClassName={rowClassName}
          />
        )}
      </div>
    </Modal>
  );
});
